<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { UiBrandMark, UiIcon } from '@fcms/ui'
import { useI18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { useAnnouncementsStore } from '@/stores/announcements'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const announcements = useAnnouncementsStore()

const title = computed(() => (route.meta.title ? t(route.meta.title) : t('app.name')))
const showBack = computed(() => Boolean(route.meta.back))

interface NavItem {
  name: string
  icon: 'home' | 'users' | 'receipt' | 'wallet' | 'user'
  label: string
}

const navItems: NavItem[] = [
  { name: 'home', icon: 'home', label: 'nav.home' },
  { name: 'queue', icon: 'users', label: 'nav.queue' },
  { name: 'bills', icon: 'receipt', label: 'nav.bills' },
  { name: 'wallet', icon: 'wallet', label: 'nav.wallet' },
  { name: 'profile', icon: 'user', label: 'nav.profile' },
]

onMounted(() => {
  if (!auth.profile) auth.loadProfile().catch(() => {})
  if (!announcements.loaded) announcements.refreshUnreadCount().catch(() => {})
})
</script>

<template>
  <div class="portal-shell">
    <header class="portal-topbar">
      <button
        v-if="showBack"
        type="button"
        class="portal-topbar__back"
        :aria-label="t('common.back')"
        @click="router.back()"
      >
        <UiIcon name="chevron-left" size="24" />
      </button>
      <UiBrandMark v-if="!showBack" height="20px" class="portal-topbar__mark" />
      <h1 class="portal-topbar__title">{{ title }}</h1>
      <div class="portal-topbar__actions">
        <RouterLink to="/announcements" class="portal-topbar__bell" :aria-label="t('nav.announcements')">
          <UiIcon name="bell" size="22" />
          <span v-if="announcements.unreadCount > 0" class="portal-topbar__bell-badge">
            {{ announcements.unreadCount > 9 ? '9+' : announcements.unreadCount }}
          </span>
        </RouterLink>
      </div>
    </header>

    <main class="portal-main">
      <slot />
    </main>

    <nav class="portal-bottom-nav" :aria-label="t('nav.home')">
      <RouterLink
        v-for="item in navItems"
        :key="item.name"
        :to="{ name: item.name }"
        class="portal-nav-item"
      >
        <UiIcon :name="item.icon" size="22" />
        <span>{{ t(item.label) }}</span>
      </RouterLink>
    </nav>
  </div>
</template>
